import { toggleArray } from '@domain-group/javascript-utils'

import useAgency from '@hooks/useAgency'
import useCampaignPortal, { useCampaignPortalForm } from '@hooks/useCampaignPortal'
import usePortalOptions from '@hooks/usePortalOptions'

import type { CampaignPortalModel } from '@models/types'

interface ToggleAllParams {
  enabled_portal_option_values: number[],
  optionValueIds: number[],
  setEntityState: (arg: { enabled_portal_option_values: number[] }) => void,
}

const toggleAll = (params: ToggleAllParams) => {
  const { optionValueIds, enabled_portal_option_values: enabledValueIds, setEntityState } = params

  const updatedValueIds = [...enabledValueIds]

  const match = optionValueIds.find(id => updatedValueIds.includes(id))

  // If we have a match select none
  if (match){
    const filtered = updatedValueIds.filter((id: number) => !optionValueIds.includes(id))
    setEntityState({ enabled_portal_option_values: filtered })
    return
  }

  // Add all unique ids
  // @ts-expect-error TS2802
  const mergedIds = [...new Set([...enabledValueIds, ...optionValueIds])]
  setEntityState({ enabled_portal_option_values: mergedIds })
}

const useCampaignPortalModal = (initCampaignPortal: CampaignPortalModel) => {
  const {
    campaign,
    campaignPortal,
    campaignPortal: { last_sync_failed: lastSyncFailed, portal_id },
    lastSyncDateObj,
    callbacks: { deleteCampaignPortal: deleteFn, updateCampaignPortal: updateFn },
    portal,
    updating: updatingCampaignPortal,
  } = useCampaignPortal(initCampaignPortal)

  // @ts-expect-error TS2339
  const isSyncing = campaign.published

  // @ts-expect-error TS2339
  const agencyPayload = useAgency({ id: campaign.agency_id })

  const {
    agency,
    callbacks: {
      updateAgency: updateAgencyFn,
    },
    updating: updatingAgency,
  } = agencyPayload

  const { filteredPortalOptions, hasFilteredPortalOptions } = usePortalOptions({
    filters: { portal_id },
    requestOptions: { include_portal_option_values: true },
    performHttpRequests: true,
  })

  const campaignPortalFormPayload = useCampaignPortalForm(campaignPortal, { validateOn: 'change' })

  const {
    entityState: {
      enabled_portal_option_values,
    },
    setEntityState,
  } = campaignPortalFormPayload

  return {
    agency,
    campaign,
    campaignPortal,
    campaignPortalFormPayload,
    callbacks: {
      deleteCampaignPortal: deleteFn,
      toggleAll: (optionValueIds: number[]) => toggleAll(
        { optionValueIds, enabled_portal_option_values, setEntityState },
      ),
      togglePortalOptionValueId: (optionValueId: number) => {
        const updatedValues = toggleArray(enabled_portal_option_values, optionValueId)
        setEntityState({ enabled_portal_option_values: updatedValues })
      },
      updateCampaignPortal: updateFn,
      updateAgency: updateAgencyFn,
    },
    filteredPortalOptions,
    hasFilteredPortalOptions,
    isSyncing,
    lastSyncDateObj,
    lastSyncFailed,
    portal,
    updatingAgency,
    updatingCampaignPortal,
  }
}

export type UseCampaignPortalModalPayload = ReturnType<typeof useCampaignPortalModal>

export default useCampaignPortalModal
